import React from "react";

import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";

import { PageProductBlock } from "@/common/contracts/page.contracts";
import { cn } from "@/common/utils/css.utils";
import Image from "@/components/common/Image";
import { Flavor } from "@/ui/typography/Flavor";

type CollectionCardProps = {
  collection: PageProductBlock;
  minimal?: boolean;
  className?: string;
  onClick?: () => void;
};

export const CollectionCard = ({
  collection,
  minimal,
  className,
  onClick,
}: CollectionCardProps) => {
  const { t } = useTranslation("collections");

  const showDescription = !minimal && collection.description;
  const showLink = !minimal && collection.listingPage;

  const renderContent = () => {
    return (
      <>
        <div
          className={cn("flex flex-col", minimal ? "space-y-2.5" : "space-y-4")}
        >
          {/*
           * CMS uploads don't all respect 3:2, so crop to it instead of letting
           * each image set its own height. Plugin padding box, not CSS
           * `aspect-ratio`, so older browsers get it too.
           */}
          <div className="aspect-h-2 aspect-w-3">
            <Image
              alt={collection.title}
              src={collection.image}
              width={900}
              height={600}
              className="object-cover"
            />
          </div>
          {minimal ? (
            <Flavor size="medium" className="text-center">
              {collection.title}
            </Flavor>
          ) : (
            <h4 className="text-2xl font-semibold">{collection.title}</h4>
          )}
          {showDescription && <p>{collection.description}</p>}
        </div>

        {showLink && (
          <div className="mt-6">
            <span className="font-poppins font-semibold text-primary-500">
              {t("collection-read-more-link")}
            </span>
          </div>
        )}
      </>
    );
  };

  const wrapperProps = {
    className: cn("flex flex-col justify-between bg-white", className),
  };

  return collection.listingPage ? (
    <Link
      href={`/${collection.listingPage.url}`}
      prefetch={false}
      {...wrapperProps}
      onClick={onClick}
    >
      {renderContent()}
    </Link>
  ) : (
    <div {...wrapperProps}>{renderContent()}</div>
  );
};
